import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrderModel from "../../../models/order/OrderModel";
import * as QuotaAction from "../../../stores/quotas/QuotaAction";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { ManageOrdersFilter } from "../../components/ManageOrdersFilter/ManageOrdersFilter";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useHistory, useParams } from "react-router-dom";
import Typography from "@material-ui/core/Typography/Typography";
import { styles } from "./QuotaOrdersView.styles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { QuotaDetails } from "../../components/QuotaDetails/QuotaDetails";
import { CsvExportButton, IColumnMapping } from "../../components/CsvExport/CsvExportButton";
import { convertDateToFullDateTime, today } from "../../../utilities/dateTimeUtils";

export interface IQuotaOrdersViewProps extends WithStyles<typeof styles> {}

const QuotaOrdersViewPure: React.FunctionComponent<IQuotaOrdersViewProps> = ({ classes }: IQuotaOrdersViewProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { quotaId } = useParams();
    const { t } = useTranslation();
    const isRequestingOrders = useSelector(state => selectRequesting(state, [QuotaAction.REQUEST_QUOTA_ORDERS]));
    const isRequestingQuota = useSelector(state => selectRequesting(state, [QuotaAction.REQUEST_QUOTA]));

    const [orderTypesChecked, setOrderTypesChecked] = useState<string[]>([]);
    const [orderStatesChecked, setOrderStatesChecked] = useState<string[]>([]);
    const allUsersFromState = useSelector(state => state.userDetails?.all);

    useEffect(() => {
        dispatch(QuotaAction.requestQuota(quotaId));
        dispatch(QuotaAction.requestQuotaOrders(quotaId));
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [dispatch, quotaId]);

    const isNothingSelected = (selectedArray: string[]) => {
        return selectedArray.length === 0;
    };

    const getUserDetails = (userId: string): any => {
        return allUsersFromState && Object.values(allUsersFromState)?.find((user: UserDetailsModel) => user.id === userId);
    };

    const handleOderTypeChange = (event: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
        setOrderTypesChecked(newTypes);
    };

    const handleOderStateChange = (event: React.MouseEvent<HTMLElement>, newStates: string[]) => {
        setOrderStatesChecked(newStates);
    };

    const handleOrderLinkClick = index => {
        const orderId = orders[index].orderId;
        const link = `/admin/manage-orders/${orderId}`;
        history.push(link);
    };

    let orders: IUniversalTableData = [];

    let ordersFromState = useSelector(state => state.quotas.orders);
    if (ordersFromState) {
        let relevantlOrders: OrderModel[] = Object.values(ordersFromState);
        if (!isNothingSelected(orderStatesChecked)) {
            relevantlOrders = relevantlOrders.filter(order => orderStatesChecked.includes(order.state));
        }
        if (!isNothingSelected(orderTypesChecked)) {
            relevantlOrders = relevantlOrders.filter(order => orderTypesChecked.includes(order.type));
        }
        orders = relevantlOrders
            ?.filter((order: OrderModel) => order.state !== "New")
            .map((order: OrderModel) => ({
                orderId: order.orderId,
                userId: order.userId,
                email: getUserDetails(order.userId)?.email,
                created: order.created,
                updated: order.updated,
                state: order.state,
                type: order.type,
                totalPrice: order.totalPrice
            }));
    }

    const allQuotas = useSelector(state => state.quotas.allQuotas);
    let quota = null;
    if (allQuotas) quota = allQuotas[quotaId];

    const columns: IUniversalTableColumn[] = [
        {
            type: "link",
            title: t("general.orderNo"),
            field: "orderId",
            onClick: index => handleOrderLinkClick(index)
        },
        {
            type: "text",
            title: t("general.user"),
            field: "email"
        },
        {
            type: "dateTime",
            title: t("general.createdOn"),
            field: "created"
        },
        {
            type: "dateTime",
            title: t("general.updatedOn"),
            field: "updated"
        },
        {
            type: "text",
            title: t("general.status"),
            field: "state"
        },
        {
            type: "text",
            title: t("general.type"),
            field: "type"
        },
        {
            type: "price",
            title: "Quota",
            field: "totalPrice"
        }
    ];

    const ordersExportMapping: IColumnMapping[] = [
        {
            label: "Order ID",
            key: "orderId"
        },
        {
            label: "User's email",
            key: "email"
        },
        {
            label: "Created on",
            key: "created",
            mappingFunc: value => convertDateToFullDateTime(value)
        },
        {
            label: "Updated on",
            key: "updated",
            mappingFunc: value => convertDateToFullDateTime(value)
        },
        {
            label: "Status",
            key: "state"
        },
        {
            label: "Type",
            key: "type"
        },
        {
            label: "Used quota",
            key: "totalPrice"
        }
    ]

    if (!quotaId) return <CenteredContainer>No quote number provided</CenteredContainer>;
    const quotaTitle = quota?.comment || quota?.id;

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Manage Quotas", href: "/admin/quotas" },
                    { title: `${quotaTitle} - Quota Orders` }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">Quota Orders</Typography>
            </div>
            <div className={classes.quotaDetails}>
                <QuotaDetails quota={quota} isLoading={isRequestingQuota} />
            </div>
            <CsvExportButton
                data={orders || []}
                outputFilename={`quota_export_${quotaTitle}_${today().format("YYYYMMDD")}.csv`}
                mappings={ordersExportMapping}
            >
                Export data
            </CsvExportButton>
            <ManageOrdersFilter
                orderTypesChecked={orderTypesChecked}
                orderStatesChecked={orderStatesChecked}
                onOrderTypeCheckboxClick={handleOderTypeChange}
                onOrderStateCheckboxClick={handleOderStateChange}
            />
            <UniversalTable
                isLoading={isRequestingOrders}
                columns={columns}
                data={orders}
                searchable
            />
        </CenteredContainer>
    );
};

export const QuotaOrdersView = withStyles(styles)(QuotaOrdersViewPure);
